import { Box, Divider, Grid, styled, Typography, TypographyProps } from '@mui/material';
import React, { PropsWithChildren } from 'react';
import { match } from 'ts-pattern';
import { defaultTheme } from '../../../../theme/default-theme';
import { KpiUiSubValue } from './types';

// Styled components
const KpiContainer = styled(Box)`
  border-radius: 4px;
  padding: 0.5rem;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background-color: ${defaultTheme.color.clientPrimary};
`;

const KpiTextBase = styled(Typography)`
  color: #ffffff;
  white-space: pre;
  text-overflow: ellipsis;
  overflow: hidden;
  display: block;
`;

const KpiText = (props: TypographyProps) => {
  return <KpiTextBase {...props} title={typeof props.children === 'string' ? props.children : undefined} />;
};

const KpiTitle = styled(KpiText)`
  font-size: 1.5rem;
  white-space: normal;
`;

const KpiSubtitle = styled(KpiText)`
  font-size: 1.2rem;
`;

const SingleColumnMainValue = styled(KpiText)`
  font-size: 1.5rem;
`;

const SingleColumnSubValue = styled(KpiText)`
  font-size: 1rem;
`;

const TwoColumnTitle = styled(KpiText)`
  font-size: 1.2rem;
`;

const TwoColumnMainValue = styled(KpiText)`
  font-size: 1.5rem;
`;

const TwoColumnSubValue = styled(KpiText)`
  font-size: 1rem;
`;

const StoryboardFooter = styled(KpiText)`
  font-size: 1rem;
`;

export interface KpiUiSingleColumnSection {
  mainValue?: string;
  subValues?: KpiUiSubValue[];
}

export interface KpiUiTwoColumnSection {
  title?: string;
  mainValue?: string;
  subValues?: KpiUiSubValue[];
}

// Common props for both UI types
interface KpiUiCommonProps {
  title?: string;
  subtitle?: string;
  footer?: string;
}

export enum KpiUiLayout {
  SINGLE_COLUMN = 'singleColumn',
  TWO_COLUMN = 'twoColumn',
}

// Single column specific options
export interface KpiUiSingleColumnOptions extends KpiUiCommonProps {
  layout: KpiUiLayout.SINGLE_COLUMN;
  section: KpiUiSingleColumnSection;
}

// Two column specific props
export interface KpiUiTwoColumnOptions extends KpiUiCommonProps {
  layout: KpiUiLayout.TWO_COLUMN;
  sections: {
    leftSection: KpiUiTwoColumnSection;
    rightSection: KpiUiTwoColumnSection;
  };
}

// Union type for all possible options
export type KpiUiOptions = KpiUiSingleColumnOptions | KpiUiTwoColumnOptions;

interface KpiUiBaseProps {
  title?: string;
  subtitle?: string;
  footer?: string;
}

const KpiUiBase = (props: PropsWithChildren<KpiUiBaseProps>) => {
  const { title, subtitle, footer, children } = props;

  return (
    <KpiContainer>
      <Grid container direction="column" sx={{ height: '100%', width: '100%' }} wrap="nowrap">
        {/* Header section */}
        {(title || subtitle) && (
          <Grid item sx={{ overflow: 'hidden' }}>
            {title && <KpiTitle>{title}</KpiTitle>}
            {subtitle && <KpiSubtitle>{subtitle}</KpiSubtitle>}
          </Grid>
        )}

        {/* Content section */}
        <Grid item container alignItems={'center'} sx={{ flexGrow: 1 }}>
          {children}
        </Grid>

        {/* Footer section */}
        {footer && (
          <Grid item>
            <StoryboardFooter>{footer}</StoryboardFooter>
          </Grid>
        )}
      </Grid>
    </KpiContainer>
  );
};

// Single column section component
const SingleColumnSection = ({ section }: { section: KpiUiSingleColumnSection }) => {
  return (
    <Grid item container direction={'column'}>
      <Grid item container sx={{ flexGrow: 1 }} direction={'column'} alignItems={'center'}>
        <SingleColumnMainValue>{section.mainValue}</SingleColumnMainValue>
        {section.subValues && section.subValues.length > 0 ? (
          <Grid item container direction={'column'}>
            {section.subValues.map((subValue, i) => (
              <Grid
                key={`${subValue.title}-${i}`}
                item
                container
                direction={'row'}
                justifyContent={'space-between'}
                alignItems={'center'}
                wrap={'nowrap'}
              >
                <SingleColumnSubValue>{subValue.title}</SingleColumnSubValue>
                <SingleColumnSubValue sx={{ flexShrink: 0 }}>{subValue.value}</SingleColumnSubValue>
              </Grid>
            ))}
          </Grid>
        ) : null}
      </Grid>
    </Grid>
  );
};

// Two column section component
const TwoColumnSection = ({ section }: { section: KpiUiTwoColumnSection }) => {
  return (
    <Grid item container direction={'column'} justifyContent={'space-between'} wrap="nowrap" sx={{ height: '100%' }}>
      <Grid item container justifyContent={'center'}>
        {section.title ? <TwoColumnTitle>{section.title}</TwoColumnTitle> : null}
      </Grid>
      <Grid item container direction={'column'} justifyContent="center" sx={{ height: '100%' }}>
        <Grid item container justifyContent={'center'}>
          <TwoColumnMainValue>{section.mainValue}</TwoColumnMainValue>
        </Grid>
        {section.subValues && section.subValues.length > 0 ? (
          <Grid item container direction={'column'}>
            {section.subValues.map((subValue, i) => (
              <Grid
                key={`${subValue.title}-${i}`}
                item
                container
                direction={'column'}
                alignItems={'center'}
                wrap={'nowrap'}
              >
                <TwoColumnSubValue>{subValue.title}</TwoColumnSubValue>
                <TwoColumnSubValue sx={{ flexShrink: 0 }}>{subValue.value}</TwoColumnSubValue>
              </Grid>
            ))}
          </Grid>
        ) : null}
      </Grid>
    </Grid>
  );
};

const KpiUiSingleColumn = (props: { options: KpiUiSingleColumnOptions }) => {
  const { options } = props;
  const { section } = options;
  return (
    <KpiUiBase title={options.title} subtitle={options.subtitle} footer={options.footer}>
      <SingleColumnSection section={section} />
    </KpiUiBase>
  );
};

const KpiUiTwoColumn = (props: { options: KpiUiTwoColumnOptions }) => {
  const { options } = props;
  const { sections } = options;
  const { leftSection, rightSection } = sections;
  return (
    <KpiUiBase title={options.title} subtitle={options.subtitle} footer={options.footer}>
      <Grid item container direction="row" sx={{ flexGrow: 1, height: '100%' }} alignItems="center" wrap="nowrap">
        <Grid item sx={{ flexGrow: 1, flexBasis: 0, height: '100%', minWidth: 0 }}>
          <TwoColumnSection section={leftSection} />
        </Grid>
        <Divider
          orientation="vertical"
          sx={{
            backgroundColor: '#ffffff',
            height: '80%',
            margin: '0 1rem',
          }}
        />
        <Grid item sx={{ flexGrow: 1, flexBasis: 0, height: '100%', minWidth: 0 }}>
          <TwoColumnSection section={rightSection} />
        </Grid>
      </Grid>
    </KpiUiBase>
  );
};
interface KpiUiProps {
  options: KpiUiOptions;
}

// Single unified KpiUi component that handles both layouts
export const KpiUi = (props: KpiUiProps) => {
  const { options } = props;
  return match(options)
    .with({ layout: KpiUiLayout.SINGLE_COLUMN }, (options) => {
      return <KpiUiSingleColumn options={options} />;
    })
    .with({ layout: KpiUiLayout.TWO_COLUMN }, (options) => {
      return <KpiUiTwoColumn options={options} />;
    })
    .exhaustive();
};
